import {Button, DatePicker, Input, Select} from 'antd';
import {useCallback, useEffect, useState} from 'react';

import {Locale} from '../../../provider/locale/locale';
import {useLocale} from '../../../provider/locale/locale-hook';
import {
    CommonEventStatusEnum,
    EVENT_AGE_LIST,
    EVENT_TYPE_LIST,
    EventAgeEnum,
    EventFormFieldsEnum,
    EventFormType,
    EventTypeEnum,
} from '../../../service/events/events-type';
import {useFormRules} from '../../../service/form/form-rules-hook';
import {Form, FormInstance} from '../../../typings/antd';
import {PublicationFieldLabel} from '../../publication-wizard/publication-field-label';
import {PublicationTitleInput} from '../../publication-wizard/publication-title-input';
import {PublicationWizardStep} from '../../publication-wizard/publication-wizard-step';
import {getEventAgeLabel, getEventTypeLangKey} from '../events-helper';

// Yandex help caps the event description at 3000 characters; the title has no documented
// limit, so we apply a moderate 100-char cap to keep titles readable.
const EVENT_TITLE_MAX_LENGTH = 100;
const EVENT_DESCRIPTION_MAX_LENGTH = 3000;

type SubmittingActionType = 'draft' | 'publish' | null;

type PropsType = {
    formInstance: FormInstance<EventFormType>;
    formMode: 'create' | 'edit';
    onPreviousStep: VoidFunction;
    onSubmit: (values: EventFormType, status: CommonEventStatusEnum) => Promise<void>;
    onStepActivated: (width: number) => void;
    width: number;
};

const rowStyle = {display: 'flex', gap: 16};

// Final wizard step: the remaining fields + submit.
export function EventDetailsStep(props: PropsType): JSX.Element {
    const {formInstance, formMode, onPreviousStep, onSubmit, onStepActivated, width} = props;

    const {getLocalizedString} = useLocale();
    const {requiredFieldRule, maxLengthFieldRule} = useFormRules();

    const [submittingAction, setSubmittingAction] = useState<SubmittingActionType>(null);

    const publishAt = Form.useWatch(EventFormFieldsEnum.PublishAt, {form: formInstance, preserve: true});

    useEffect(() => {
        onStepActivated(width);
    }, [onStepActivated, width]);

    const handleSubmit = useCallback(
        async (status: CommonEventStatusEnum) => {
            setSubmittingAction(status === CommonEventStatusEnum.Draft ? 'draft' : 'publish');

            try {
                await formInstance.validateFields();
                await onSubmit(formInstance.getFieldsValue(true), status);
            } finally {
                setSubmittingAction(null);
            }
        },
        [formInstance, onSubmit]
    );

    const isBusy = submittingAction !== null;
    const activeStatus = publishAt ? CommonEventStatusEnum.Planned : CommonEventStatusEnum.Sent;
    const publishLangKey = publishAt ? 'BUTTON__SCHEDULE' : 'BUTTON__PUBLISH';

    return (
        <PublicationWizardStep
            footer={
                formMode === 'create' ? (
                    <Button
                        disabled={isBusy}
                        loading={submittingAction === 'draft'}
                        onClick={() => handleSubmit(CommonEventStatusEnum.Draft)}
                    >
                        <Locale stringKey="POSTS_FORM__BUTTON__SAVE_DRAFT" />
                    </Button>
                ) : null
            }
            leftFooterButtonConfig={{langKeyType: 'BUTTON__BACK', onClick: onPreviousStep}}
            rightFooterButtonConfig={{
                disabled: isBusy,
                langKeyType: formMode === 'create' ? publishLangKey : 'BUTTON__UPDATE',
                loading: submittingAction === 'publish',
                onClick: () => handleSubmit(activeStatus),
            }}
        >
            <Form.Item<EventFormType>
                label={<Locale stringKey="EVENTS__FORM__TITLE" />}
                name={EventFormFieldsEnum.Title}
                rules={[requiredFieldRule, maxLengthFieldRule(EVENT_TITLE_MAX_LENGTH)]}
            >
                <PublicationTitleInput maxLength={EVENT_TITLE_MAX_LENGTH} />
            </Form.Item>

            <Form.Item<EventFormType>
                label={<Locale stringKey="EVENTS__FORM__DESCRIPTION" />}
                name={EventFormFieldsEnum.Description}
                rules={[requiredFieldRule, maxLengthFieldRule(EVENT_DESCRIPTION_MAX_LENGTH)]}
            >
                <Input.TextArea
                    autoSize={{minRows: 3, maxRows: 8}}
                    maxLength={EVENT_DESCRIPTION_MAX_LENGTH}
                    showCount
                />
            </Form.Item>

            <div style={rowStyle}>
                <Form.Item<EventFormType>
                    label={<Locale stringKey="EVENTS__FORM__TYPE" />}
                    name={EventFormFieldsEnum.EventType}
                    rules={[requiredFieldRule]}
                    style={{flex: 1}}
                >
                    <Select<EventTypeEnum>
                        optionFilterProp="label"
                        options={EVENT_TYPE_LIST.map((eventType) => ({
                            label: getLocalizedString(getEventTypeLangKey(eventType)),
                            value: eventType,
                        }))}
                    />
                </Form.Item>

                <Form.Item<EventFormType>
                    label={<Locale stringKey="EVENTS__FORM__AGE" />}
                    name={EventFormFieldsEnum.MinAge}
                    rules={[requiredFieldRule]}
                    style={{flex: 1}}
                >
                    <Select<EventAgeEnum>
                        options={EVENT_AGE_LIST.map((age) => ({label: getEventAgeLabel(age), value: age}))}
                    />
                </Form.Item>
            </div>

            <div style={rowStyle}>
                <Form.Item<EventFormType>
                    label={
                        <PublicationFieldLabel
                            labelLangKey="EVENTS__FORM__START_AT"
                            tooltipLangKey="EVENTS__FORM__START_AT__TOOLTIP"
                        />
                    }
                    name={EventFormFieldsEnum.StartAt}
                    rules={[requiredFieldRule]}
                    style={{flex: 1}}
                >
                    <DatePicker showTime style={{width: '100%'}} />
                </Form.Item>

                <Form.Item<EventFormType>
                    label={
                        <PublicationFieldLabel
                            labelLangKey="EVENTS__FORM__FINISH_AT"
                            tooltipLangKey="EVENTS__FORM__FINISH_AT__TOOLTIP"
                        />
                    }
                    name={EventFormFieldsEnum.FinishAt}
                    rules={[requiredFieldRule]}
                    style={{flex: 1}}
                >
                    <DatePicker showTime style={{width: '100%'}} />
                </Form.Item>
            </div>

            <div style={rowStyle}>
                <Form.Item<EventFormType>
                    label={
                        <PublicationFieldLabel
                            labelLangKey="EVENTS__FORM__PUBLISH_AT"
                            tooltipLangKey="EVENTS__FORM__PUBLISH_AT__TOOLTIP"
                        />
                    }
                    name={EventFormFieldsEnum.PublishAt}
                    style={{flex: 1}}
                >
                    <DatePicker showTime style={{width: '100%'}} />
                </Form.Item>

                <Form.Item<EventFormType>
                    label={
                        <PublicationFieldLabel
                            labelLangKey="EVENTS__FORM__DELETE_AT"
                            tooltipLangKey="EVENTS__FORM__DELETE_AT__TOOLTIP"
                        />
                    }
                    name={EventFormFieldsEnum.DeletedAt}
                    style={{flex: 1}}
                >
                    <DatePicker showTime style={{width: '100%'}} />
                </Form.Item>
            </div>
        </PublicationWizardStep>
    );
}
